import React, { useCallback, useMemo } from "react";
import { BaseWidgetProps } from "../BaseProps";

import { graphql, useFragment } from "react-relay";

import { WidgetFile_widgetFile$key } from "./__generated__/WidgetFile_widgetFile.graphql";
import { WidgetFileDynamic } from "./WidgetFileDynamic";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { WidgetFileSubscription } from "./__generated__/WidgetFileSubscription.graphql";
import { WidgetFileStatic } from "./WidgetFileStatic";
import { useSetWidgetFocus } from "../useSetFocus";

const conf = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};

export const WidgetFile = (
  props: {
    widget: WidgetFile_widgetFile$key;
    pageId?: string;
  } & BaseWidgetProps
) => {
  const widget = useFragment(
    graphql`
      fragment WidgetFile_widgetFile on WidgetFile {
        id
        files {
          file {
            id
            status
          }
        }
        ...WidgetFileStatic_widgetFile
        ...WidgetFileDynamic_widgetFile
      }
    `,
    props.widget
  );

  const fileIds = useMemo(
    () => widget.files?.map((f) => f?.file?.id) || [],
    [widget.files]
  );
  useSubscriptionWithSkip<WidgetFileSubscription>(
    useMemo(
      () => ({
        variables: {
          ids: fileIds,
        },
        subscription: graphql`
          subscription WidgetFileSubscription($ids: [ID!]!) {
            fileUpdate(ids: $ids) {
              fileWidgets {
                ...WidgetFileStatic_widgetFile
                ...WidgetFileDynamic_widgetFile
              }
            }
          }
        `,
      }),
      [fileIds]
    ),
    {
      skip: fileIds.length === 0,
    }
  );

  const focus = useSetWidgetFocus(widget.id, props.storecastId);

  const { onEvent, mode } = props;
  const onPressItem = useCallback(
    (fileId: string, pageId: string) => {
      if (mode === "FOCUS") {
        focus(pageId);
      } else {
        onEvent?.({
          kind: "FileOpen",
          fileId,
          pageId,
        });
      }
    },
    [mode, onEvent, focus]
  );

  const { widget: _, ...rest } = props;
  if (props.idiom === "STATIC" || props.idiom === "TOUCH_READONLY") {
    return (
      <WidgetFileStatic
        widget={widget}
        {...rest}
        isTouch={props.idiom === "TOUCH_READONLY"}
        onPagePress={onPressItem}
        pageId={props.pageId}
        isFocusMode={props.isFocused}
      />
    );
  } else {
    return (
      <WidgetFileDynamic
        widget={widget}
        {...rest}
        onPagePress={onPressItem}
        pageId={props.pageId}
      />
    );
  }
};
